import React, { useState, useEffect, useCallback } from 'react';
import { fmt, api } from '../utils/api';
import CollapsiblePanel from './CollapsiblePanel';

export default function InvestmentGoal({ defaultCollapsed, refreshKey, onRefresh }) {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState('flat'); // 'flat' | 'percent'
  const [goalInput, setGoalInput] = useState('');
  const [pctInput, setPctInput] = useState(3);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.getInvestments();
      setData(d);
      setGoalInput(d.monthlyInvestmentGoal || 0);
      setMode(d.investmentGoalMode || 'flat');
      setPctInput(d.investmentGoalPct || 3);
    } catch (err) {
      console.error(err);
    }
  }, []);

  // Refetch on any app-wide change too - a synced account can shift the
  // investment balance this goal is tracked against
  useEffect(() => { load(); }, [load, refreshKey]);

  const saveGoal = async () => {
    const amount = parseFloat(goalInput);
    if (isNaN(amount) || amount < 0) return;
    setSaving(true);
    try {
      await api.updateInvestmentGoal(amount);
      load();
      onRefresh?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const savePct = async (pct) => {
    setSaving(true);
    try {
      const result = await api.updateInvestmentGoalPercent(pct);
      setData((d) => ({ ...d, monthlyInvestmentGoal: result.monthlyInvestmentGoal, investmentGoalMode: 'percent', investmentGoalPct: pct }));
      onRefresh?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <CollapsiblePanel title="📈 Investment Contribution Goal" defaultCollapsed={defaultCollapsed}>
      <p style={styles.helpText}>
        Set how much you want to invest each month. Your budget will
        automatically make room for it by reducing what's available to
        spend - and we'll check your actual investment balance growth to
        confirm you followed through.
      </p>

      {!data ? (
        <p style={styles.empty}>Loading...</p>
      ) : (
        <>
          <div style={styles.modeToggle}>
            <button
              style={{ ...styles.modeBtn, ...(mode === 'flat' ? styles.modeBtnActive : {}) }}
              onClick={() => setMode('flat')}
            >
              $ Flat Amount
            </button>
            <button
              style={{ ...styles.modeBtn, ...(mode === 'percent' ? styles.modeBtnActive : {}) }}
              onClick={() => setMode('percent')}
            >
              % of Income
            </button>
          </div>

          {mode === 'flat' ? (
            <div style={styles.inputRow}>
              <span style={styles.dollarSign}>$</span>
              <input
                type="number"
                min="0"
                step="10"
                style={styles.input}
                value={goalInput}
                onChange={(e) => setGoalInput(e.target.value)}
              />
              <span style={styles.perMo}>/mo</span>
              <button style={styles.saveBtn} onClick={saveGoal} disabled={saving}>
                {saving ? '...' : 'Save'}
              </button>
            </div>
          ) : (
            <div style={styles.pctBox}>
              <div style={styles.pctRow}>
                <input
                  type="range"
                  min="0"
                  max="20"
                  step="0.5"
                  style={styles.slider}
                  value={pctInput}
                  onChange={(e) => setPctInput(parseFloat(e.target.value))}
                  onMouseUp={() => savePct(pctInput)}
                  onTouchEnd={() => savePct(pctInput)}
                  onKeyUp={() => savePct(pctInput)}
                />
                <span style={styles.pctValue}>{pctInput}%</span>
              </div>
              <p style={styles.pctHelp}>
                {pctInput}% of your projected gross income ≈ {fmt.currency(data.monthlyInvestmentGoal)}/mo.
                Recalculates against your current income projection each time you adjust it.
              </p>
            </div>
          )}

          {data.monthlyInvestmentGoal > 0 && (
            <div style={styles.statusBox}>
              <div style={styles.statusRow}>
                <span style={styles.statusLabel}>This month's growth</span>
                <span style={styles.statusValue}>{fmt.currency(data.investmentGrowthThisMonth)}</span>
              </div>
              {Math.abs(data.investmentTransfersOutThisMonth || 0) > 0.5 && (
                <p style={styles.transferNote}>
                  Excludes {fmt.currency(Math.abs(data.investmentTransfersOutThisMonth))} {data.investmentTransfersOutThisMonth > 0 ? 'withdrawn' : 'contributed'} this month - see Investments for the full breakdown.
                </p>
              )}
              <div style={styles.statusRow}>
                <span style={styles.statusLabel}>Goal</span>
                <span style={styles.statusValue}>{fmt.currency(data.monthlyInvestmentGoal)}</span>
              </div>
              <div style={{
                ...styles.badge,
                color: data.investmentGoalMet ? '#00d4aa' : '#ef4444',
                borderColor: data.investmentGoalMet ? '#00d4aa' : '#ef4444',
              }}>
                {data.investmentGoalMet ? '✓ Goal met' : '✗ Goal not met yet'}
              </div>
              <p style={styles.disclaimer}>
                Based on actual investment account balance growth this month
                (via connected accounts) - not just an assumption.
              </p>
            </div>
          )}
        </>
      )}
    </CollapsiblePanel>
  );
}

const styles = {
  helpText: {
    color: '#6b7280',
    fontSize: '0.8rem',
    lineHeight: '1.5',
    marginBottom: '1rem',
  },
  empty: { color: '#4b5563', fontSize: '0.8rem', margin: 0 },
  modeToggle: {
    display: 'flex',
    gap: '0.4rem',
    marginBottom: '0.85rem',
  },
  modeBtn: {
    flex: 1,
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#6b7280',
    borderRadius: '8px',
    padding: '0.5rem',
    fontSize: '0.78rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  modeBtnActive: {
    background: 'rgba(0, 212, 170, 0.12)',
    borderColor: '#00d4aa',
    color: '#00d4aa',
  },
  pctBox: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '1rem',
    marginBottom: '1rem',
  },
  pctRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  slider: {
    flex: 1,
    accentColor: '#00d4aa',
  },
  pctValue: {
    color: '#fff',
    fontSize: '1rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
    minWidth: '48px',
    textAlign: 'right',
  },
  pctHelp: {
    color: '#6b7280',
    fontSize: '0.72rem',
    lineHeight: '1.5',
    marginTop: '0.6rem',
    marginBottom: 0,
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginBottom: '1rem',
  },
  dollarSign: { color: '#6b7280', fontSize: '0.9rem' },
  input: {
    flex: 1,
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.6rem 0.75rem',
    color: '#fff',
    fontSize: '0.9rem',
    fontFamily: "'DM Mono', monospace",
  },
  perMo: { color: '#6b7280', fontSize: '0.8rem' },
  saveBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '8px',
    padding: '0.6rem 1rem',
    fontSize: '0.8rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  statusBox: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '1rem',
  },
  statusRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.8rem',
    marginBottom: '0.4rem',
  },
  transferNote: {
    color: '#fbbf24',
    fontSize: '0.68rem',
    lineHeight: '1.4',
    margin: '0 0 0.6rem',
  },
  statusLabel: { color: '#9ca3af', fontFamily: "'Syne', sans-serif" },
  statusValue: { color: '#e5e7eb', fontFamily: "'DM Mono', monospace" },
  badge: {
    display: 'inline-block',
    border: '1px solid',
    borderRadius: '999px',
    padding: '3px 10px',
    fontSize: '0.75rem',
    fontWeight: '700',
    fontFamily: "'Syne', sans-serif",
    marginTop: '0.5rem',
  },
  disclaimer: {
    color: '#4b5563',
    fontSize: '0.7rem',
    lineHeight: '1.4',
    marginTop: '0.6rem',
    marginBottom: 0,
  },
};
